import React, { Component } from 'react';
import './Home.css';
import preloadImages from '../lib/preloadImages';
import FadeInSection from '../components/shared/FadeInSection';
import ImageSlideshow from '../components/shared/ImageSlideshow';
import RotatingWord from '../components/shared/RotatingWord';
import ScrollingNumber from '../components/shared/ScrollingNumber';

const IMAGES_TO_PRELOAD = [
  '/images/profileimg/rochester/franciscabarria.webp',
  '/images/profileimg/rochester/avabrophy.webp',
  '/images/profileimg/rochester/mihikabiswas.webp',
  '/images/profileimg/rochester/nancyfan.webp',
  '/images/mentee-testimonial/MUHYA MUTABAZI ROSALIE.webp',
];

const STATS = [
  { value: '84',    label: 'Mentors',          subtitle: 'Dedicated volunteers across 4 university chapters' },
  { value: '129',   label: 'Active Mentees',   subtitle: 'Students receiving personalized guidance' },
  { value: '715',   label: 'Students Reached', subtitle: 'Through events and programs nationwide' },
  { value: '12',    label: 'Local Events',      subtitle: 'Engaging with communities directly' },
  { value: '13000', label: 'Funds Raised',      subtitle: 'Supporting our mission and programs', prefix: '$' },
  { value: '95',    label: 'Satisfaction Rate', subtitle: 'Students who would recommend LTF', suffix: '%' },
];

const ROTATING_WORDS = ['FREE', 'Personalized', 'Accessible', 'High-Quality', 'FREE', 'Personalized', 'Accessible', 'High-Quality'];

class Home extends Component {
  constructor(props) {
    super(props);
    this.state = {
      heroVideoReady: false,
      heroVideoVisible: false,
      showDonationWidgets: false,
    };
    this.heroObserver = null;
    this.heroRef = React.createRef();
  }

  componentDidMount() {
    preloadImages(IMAGES_TO_PRELOAD);

    if (this.heroRef.current && 'IntersectionObserver' in window) {
      this.heroObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.setState({ heroVideoVisible: true });
            this.heroObserver.disconnect();
          }
        },
        { threshold: 0.35 }
      );
      this.heroObserver.observe(this.heroRef.current);
    } else {
      this.setState({ heroVideoVisible: true });
    }
  }

  componentWillUnmount() {
    if (this.heroObserver) {
      this.heroObserver.disconnect();
    }
  }

  handleVideoLoaded = () => this.setState({ heroVideoReady: true });

  handleShowDonations = () => this.setState({ showDonationWidgets: true });

  render() {
    const { heroVideoReady, heroVideoVisible, showDonationWidgets } = this.state;

    return (
      <div className="container-fluid">

        {/* Hero — full-screen background video */}
        <FadeInSection>
          <div className="welcome-section" ref={this.heroRef}>
            <img
              src="/images/generalimg/background.webp"
              alt="Mentors at work"
              className={`background-video__placeholder ${heroVideoReady ? 'is-hidden' : ''}`}
              loading="lazy"
            />
            {heroVideoVisible && (
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/images/generalimg/background.webp"
                className={`background-video ${heroVideoReady ? 'is-visible' : ''}`}
                onLoadedData={this.handleVideoLoaded}
              >
                <source src="/images/generalimg/MOV_8612.mov" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </FadeInSection>

        {/* Mission statement over team photo */}
        <FadeInSection delay={200}>
          <div className="student-led-section">
            <img
              src="/images/generalimg/teampic.webp"
              alt="LTF team"
              className="student-led-image"
              loading="lazy"
            />
            <div className="student-led-text">
              <h1>
                A student-led nonprofit dedicated to providing{' '}
                <RotatingWord words={ROTATING_WORDS} /> college application resources.
              </h1>
            </div>
          </div>
        </FadeInSection>

        {/* Image slideshow */}
        <FadeInSection delay={200}>
          <ImageSlideshow />
        </FadeInSection>

        {/* Impact stats */}
        <FadeInSection delay={200}>
          <div className="stats-section">
            <div className="stats-grid">
              {STATS.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-value">
                    <ScrollingNumber
                      value={stat.value}
                      prefix={stat.prefix || ''}
                      suffix={stat.suffix || ''}
                      duration={2000}
                    />
                  </div>
                  <h3 className="stat-label">{stat.label}</h3>
                  <p className="stat-subtitle">{stat.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        </FadeInSection>

        {/* Annual Report 2025 */}
        <FadeInSection delay={200}>
          <div className="annual-report-section">
            <h2 className="annual-report-heading">Annual Report 2025</h2>
            <div
              className="annual-report-embed"
              style={{
                position: 'relative',
                width: '100%',
                height: 0,
                paddingTop: '129.4118%',
                overflow: 'hidden',
                borderRadius: '8px',
                boxShadow: '0 2px 8px 0 rgba(63,69,81,0.16)',
                marginTop: '1.6em',
                marginBottom: '0.9em',
                willChange: 'transform',
              }}
            >
              <iframe
                loading="lazy"
                title="Project Level the Field Annual Report 2025"
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  border: 'none',
                  padding: 0,
                  margin: 0,
                }}
                src="https://www.canva.com/design/DAG9rqQBW2Q/8dwOND-Iw3Iw1MWhg8-qag/view?embed"
                allowFullScreen
                allow="fullscreen"
              />
            </div>
            <a
              href="https://www.canva.com/design/DAG9rqQBW2Q/8dwOND-Iw3Iw1MWhg8-qag/view?utm_content=DAG9rqQBW2Q&utm_campaign=designshare&utm_medium=embeds&utm_source=link"
              target="_blank"
              rel="noopener noreferrer"
              className="annual-report-link"
            >
              Project Level the Field Annual Report 2025
            </a>
          </div>
        </FadeInSection>

        {/* Donation / Support section */}
        <FadeInSection delay={200}>
          <div className="support-section">
            <h2 className="support-section__heading">Watch Impact Bloom</h2>
            <p className="support-section__body">
              Help us continue providing free, high-quality college application resources and mentorship
              to students nationwide. Every donation directly supports our mission to level the playing
              field in higher education.
            </p>

            {showDonationWidgets ? (
              <>
                <div className="support-section__widgets">
                  <givebutter-widget id="j9o7wg"></givebutter-widget>
                </div>
                <div className="support-section__fundraiser">
                  <givebutter-widget id="pXBGnL"></givebutter-widget>
                </div>
              </>
            ) : (
              <button className="donation-toggle" onClick={this.handleShowDonations}>
                Show donation options
              </button>
            )}

            <p className="support-section__disclaimer">
              Project LTF is a 501(c)(3) nonprofit organization. Your donation is tax-deductible.
            </p>
          </div>
        </FadeInSection>

      </div>
    );
  }
}

export default Home;
